/**
 * Address Management Service
 * Handles all address-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';
import { handleApiError } from '../utils/errorHandler';

class AddressService {
  /**
   * Get all user addresses
   * @returns {Promise<Array>} List of addresses
   */
  async getAddresses() {
    try {
      const response = await axios.get(API_ENDPOINTS.ADDRESSES.LIST);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to fetch addresses');
    }
  }

  /**
   * Add new address
   * @param {Object} addressData - Address data
   * @param {string} addressData.name - Address label (e.g., "Home", "Office") (required)
   * @param {string} addressData.phone - Contact phone number (required)
   * @param {string} addressData.address - Full street address (required)
   * @param {number} addressData.latitude - GPS latitude (optional)
   * @param {number} addressData.longitude - GPS longitude (optional)
   * @param {boolean} addressData.is_default - Set as default address (optional)
   * @param {string} addressData.address_type - Type: home/office/other (optional)
   * @param {string} addressData.landmark - Nearby landmark (optional)
   * @param {string} addressData.pincode - Postal code (optional)
   * @param {string} addressData.city - City name (optional)
   * @param {string} addressData.state - State name (optional)
   * @returns {Promise<Object>} Created address object
   */
  async addAddress(addressData) {
    try {
      const payload = {
        name: addressData.name || addressData.address_line1 || 'Home',
        phone: addressData.phone || addressData.mobile,
        address: addressData.address || addressData.address_line2 || '',
        address_type: addressData.address_type || 'home',
      };

      // Add optional fields if provided
      if (addressData.latitude !== undefined && addressData.latitude !== null) {
        payload.latitude = parseFloat(addressData.latitude);
      }
      if (addressData.longitude !== undefined && addressData.longitude !== null) {
        payload.longitude = parseFloat(addressData.longitude);
      }
      if (addressData.is_default !== undefined) {
        payload.is_default = addressData.is_default;
      }
      if (addressData.landmark) {
        payload.landmark = addressData.landmark;
      }
      if (addressData.pincode) {
        payload.pincode = addressData.pincode;
      }
      if (addressData.city) {
        payload.city = addressData.city;
      }
      if (addressData.state) {
        payload.state = addressData.state;
      }

      const response = await axios.post(API_ENDPOINTS.ADDRESSES.ADD, payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to add address');
    }
  }

  /**
   * Edit existing address
   * @param {number} addressId - Address ID
   * @param {Object} addressData - Updated address data (all fields optional)
   * @returns {Promise<Object>} Updated address object
   */
  async editAddress(addressId, addressData) {
    try {
      const response = await axios.put(
        API_ENDPOINTS.ADDRESSES.EDIT(addressId),
        addressData
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to edit address');
    }
  }

  /**
   * Delete address
   * @param {number} addressId - Address ID
   * @returns {Promise<Object>} Response with message
   */
  async deleteAddress(addressId) {
    try {
      const response = await axios.delete(
        API_ENDPOINTS.ADDRESSES.DELETE(addressId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Failed to delete address');
    }
  }
}

export default new AddressService();
